import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';

export interface TraceCodeDetail {
  id: string;
  rawIngredientId: string;
  receivingLogId: string;
  internalTraceCode: string;
  quantity: number;
  quantityUnit: string;
  receivingDate: string;
}

export interface GroupedRawIngredient {
  ingredientName: string;
  unitSize: string;
  totalQuantity: number;
  lotCount: number;
  traceCodes: TraceCodeDetail[];
}

/**
 * Selector that groups raw ingredients by name and joins with trace codes
 *
 * Data flow:
 * - raw_ingredients → receivingLogId → ingredient_receiving
 * - ingredient_receiving ← ingredientReceivingId ← ingredient_trace_codes
 *
 * Returns grouped ingredients with aggregated totals and trace code details
 */
export const selectGroupedRawIngredients = createSelector(
  [
    (state: RootState) => state.rawIngredients.ingredients,
    (state: RootState) => state.ingredientTraceCodes.traceCodes,
    (state: RootState) => state.ingredientReceiving.entries
  ],
  (rawIngredients, traceCodes, receivingEntries) => {
    // Create a map for quick receiving entry lookups
    const receivingMap = new Map(receivingEntries.map(entry => [entry.id, entry]));

    // Build intermediate structure: raw ingredient with its trace codes
    const rawIngredientsWithTraceCodes = rawIngredients.map(rawIng => {
      const receivingEntry = receivingMap.get(rawIng.receivingLogId);

      // Find all trace codes for this receiving entry
      const relatedTraceCodes = traceCodes
        .filter(tc => tc.ingredientReceivingId === rawIng.receivingLogId)
        .map(tc => ({
          id: tc.id,
          rawIngredientId: rawIng.id,
          receivingLogId: rawIng.receivingLogId,
          internalTraceCode: tc.internalTraceCode,
          quantity: receivingEntry?.quantity || 0,
          quantityUnit: receivingEntry?.unit || '',
          receivingDate: receivingEntry?.receivingDate || ''
        }));

      return {
        rawIngredient: rawIng,
        traceCodes: relatedTraceCodes,
        receivingEntry
      };
    });

    // Group by ingredient name
    const groupedMap = new Map<string, GroupedRawIngredient>();

    rawIngredientsWithTraceCodes.forEach(({ rawIngredient, traceCodes, receivingEntry }) => {
      const ingredientName = rawIngredient.name;

      if (!groupedMap.has(ingredientName)) {
        groupedMap.set(ingredientName, {
          ingredientName,
          unitSize: rawIngredient.unitSize || receivingEntry?.unit || '',
          totalQuantity: 0,
          lotCount: 0,
          traceCodes: []
        });
      }

      const group = groupedMap.get(ingredientName)!;

      // Add trace codes to the group
      group.traceCodes.push(...traceCodes);

      // Update lot count
      group.lotCount = group.traceCodes.length;

      // Sum quantities from receiving entries
      if (receivingEntry) {
        group.totalQuantity += receivingEntry.quantity;
      }
    });

    // Convert map to array and sort by ingredient name
    return Array.from(groupedMap.values()).sort((a, b) =>
      a.ingredientName.localeCompare(b.ingredientName)
    );
  }
);
